import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { KeycloakAuthGuard, KeycloakService } from 'keycloak-angular';

import { environment } from 'src/environments/environment';

/**
 * Keycloak-backed route guard.  Redirects unauthenticated users to Keycloak login and grants
 * access when the user holds **any** of the roles listed in `route.data.roles` (OR logic).
 */
@Injectable({
  providedIn: 'root',
})
export class AuthGuard extends KeycloakAuthGuard {
  constructor(
    protected readonly router: Router,
    protected readonly keycloak: KeycloakService
  ) {
    super(router, keycloak);
  }

  /** Redirects to Keycloak login if unauthenticated; returns `true` when the user has at least one of the route's required roles (OR logic). */
  public async isAccessAllowed(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ) {
    if (!this.authenticated) {
      await this.keycloak.login({
        redirectUri: window.location.origin + state.url,
      });
    }

    const requiredRoles = route.data.roles;

    if (!(requiredRoles instanceof Array) || requiredRoles.length === 0) {
      return true;
    }

    return requiredRoles.some((role) => this.roles.includes(role));
  }

  /** Returns the Keycloak client ID for the current session. */
  getLoggedUserInfo(): any {
    return this.keycloak.getKeycloakInstance().clientId;
  }

  /** Returns the Keycloak username of the authenticated user. */
  getLoggedUserName(): string {
    return this.keycloak.getUsername();
  }

  /** Ends the Keycloak session and redirects to the logout URL. */
  logout() {
    return this.keycloak.logout();
  }

  /** Full catalogue of Keycloak realm/client roles recognised by this application. */
  roles: string[] = [
    // Realm Management
    "manage-realm",          // Full realm administration
    "realm-admin",           // Realm administrator (combines multiple permissions)
    "manage-users",          // Create, update, delete users
    "view-users",            // Read-only access to user data
    "query-users",           // Query user information

    // Client Management
    "manage-clients",        // Create, update, delete clients
    "view-clients",          // Read-only access to clients
    "query-clients",         // Query client information

    // Identity Provider Management
    "manage-identity-providers",  // Configure external IdPs (SAML, OIDC, etc.)
    "view-identity-providers",    // View IdP configurations

    // User Management (Self-Service)
    "manage-account",        // Users can manage their own account
    "manage-account-links",  // Link social/external accounts
    "view-profile",          // View own profile
    "delete-account",        // User can delete their own account
    "manage-consent",        // Manage application consent
    "view-consent",          // View consent history

    // Authorization & Permissions
    "manage-authorization",  // Configure fine-grained permissions
    "view-authorization",    // View authorization settings
    "uma_authorization",     // User-Managed Access (UMA) protocol
    "uma_protection",        // UMA resource protection

    // Events & Auditing
    "manage-events",         // Configure event listeners
    "view-events",           // View audit logs

    // Groups
    "query-groups",          // Query group information
    "view-groups",           // View group hierarchy

    // Realms
    "query-realms",          // Query realm information

    // Token Management
    "read-token",            // Read access tokens
    "impersonation",         // Admin can impersonate users (security-sensitive!)
  ];

  /** Returns the live Keycloak role list for the current user from `KeycloakService`. */
  getRoles(): string[] {
    return this.keycloak.getUserRoles();
  }

  /** Returns the Keycloak roles of the current user via {@link getRoles}. */
  public getCurrentUserKeycloakRoles(): string[] {
    return this.getRoles();
  }

  /**
   * Returns `true` if the current user holds a role that permits Keycloak user management.
   * @returns `true` when the user has `manage-users` or `view-users`.
   *
   * Required roles (in order of preference):
   * 1. user-manager (custom role) - recommended for user management without full admin rights
   * 2. manage-users (Keycloak built-in) - allows full user CRUD operations
   * 3. portal-admin (legacy) - for backward compatibility
   */
  public canManageKeycloakUsers(): boolean {
    const currentUserRoles = this.getCurrentUserKeycloakRoles();
    const requiredRoles = [
      'manage-users',
      'view-users',
    ];

    // environment.production || console.log('*** AuthGuard KeycloakUsers: Current user roles: ' + JSON.stringify(currentUserRoles));
    return currentUserRoles.some(role => requiredRoles.includes(role));
  }

  /** Returns `true` if the current user holds the `can-manage-users` role, granting jsreport user-management rights. */
  public canManageJSreportUsers(): boolean {
    const currentUserRoles = this.getCurrentUserKeycloakRoles();
    const requiredRoles = [
      'can-manage-users',    // Custom role for JSreport user management
    ];
    // environment.production || console.log('*** AuthGuard JSreportUsers: Current user roles: ' + JSON.stringify(currentUserRoles));
    return currentUserRoles.some(role => requiredRoles.includes(role));
  }

  /** `true` when the current user can manage Keycloak users (delegates to `canManageKeycloakUsers`). */
  public get hasKeycloakManagementRights(): boolean {
    return this.canManageKeycloakUsers();
  }

  /** Returns `true` when the current user holds at least one of `requiredRoles`. */
  public hasSomeKeycloakRoles(
    requiredRoles: string[]
  ) {
    const userRoles = this.getCurrentUserKeycloakRoles();

    return requiredRoles.some((role) => userRoles.includes(role));
  }

  /** Returns `true` when the current user holds every role in `requiredRoles`. */
  public hasAllKeycloakRoles(
    requiredRoles: string[]
  ) {

    const userRoles = this.getCurrentUserKeycloakRoles();
    return requiredRoles.every((role) => userRoles.includes(role));
  }
}

/* import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot } from '@angular/router';

const defaultPath = '/';

@Injectable()
export class AuthService {
  private _loggedIn: boolean = true;
  get loggedIn(): boolean {
    return this._loggedIn;
  }

  private _lastAuthenticatedPath: string = defaultPath;
  set lastAuthenticatedPath(value: string) {
    this._lastAuthenticatedPath = value;
  }

  constructor(private router: Router) { }

  logIn(login: string, password: string) {
    this._loggedIn = true;
    this.router.navigate([this._lastAuthenticatedPath]);
  }

  logOut() {
    this._loggedIn = false;
    this.router.navigate(['/login-form']);
  }
}

@Injectable()
export class AuthGuardService implements CanActivate {
  constructor(private router: Router, private authService: AuthService) { }

  canActivate(route: ActivatedRouteSnapshot): boolean {
    console.log('*** ActivatedRouteSnapshot canActivate');
    const isLoggedIn = this.authService.loggedIn;
    const isLoginForm = route.routeConfig.path === 'login-form';

    if(isLoggedIn && isLoginForm) {
      this.authService.lastAuthenticatedPath = defaultPath;
      this.router.navigate([defaultPath]);
      return false;
    }

    if(!isLoggedIn && !isLoginForm) {
      this.router.navigate(['/login-form']);
    }

    if(isLoggedIn) {
      this.authService.lastAuthenticatedPath = route.routeConfig.path;
    }

    return isLoggedIn || isLoginForm;
  }
}
*/
